import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

import { goFetch } from "@/utils/network-utils";
import { retryOnceOnServiceUnavailable } from "@/utils/service-unavailable-retry";
import { clientStateManager } from "@/services/error-services";
import type { ContentCatalogRequest } from "@/services/data/sync/content-sync";
import type {
    Category,
    ContentfulItem,
    ContentfulWebinar,
    Countable,
    NormalizedContent,
    Product,
    Webinar
} from "@tmi/types/entities";

import type { Webinars } from "@tmi/types/api/content-models";

import { useApplicationStore } from "@/stores/Application";
import { useAuthStore } from "@/stores/Auth";
import { usePermissionsStore } from "@/stores/Permissions";
import { isFreelyAvailableContent } from "@/utils/access-utils";
import { formatDateAsISO8601String } from "@tmi/utils/date-time";
import { type MarketInstant, toMarketTime } from "@tmi/utils/market-clock";
import { webinarPhase, webinarStartTime } from "@tmi/utils/webinar-phase";

import { cache, CacheClosedError, webinars } from "@/services/cache";
import { createInitLatch } from "@/stores/init-latch";
import { createCacheReload } from "@/stores/cache-reload";
import { createLoadStatus } from "@/stores/load-status";

import { normalizeWebinar } from "@/services/data/sync/webinars-sync-coordinator";
import { hydrateContent } from "@/services/data/sync/content-hydration";
import { DateTime } from "luxon";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// State values
const isLoading = computed< boolean >( () => loading.value || fetching.value );

const fetching = ref< boolean >( false );
const loading = ref< boolean >( false );
// tmi-7be: a cache failure stops initialization until reset(), so no watcher can re-run it in a loop.
const initLatch = createInitLatch();
// data-12: whether the list load failed, so the webinar search says "couldn't load" rather than "No results found".
const listLoad = createLoadStatus();

const initialized = computed< boolean >( () => allWebinars.value.length > 0 );
const allWebinars = ref< Webinar[] >( [] as unknown as Webinar[] );

export const useWebinarsStore = defineStore( 'webinars', () => {
    const permissionsStore = usePermissionsStore();

    const appStore = useApplicationStore();
    const { authToken, isAuthenticated } = storeToRefs( useAuthStore() );

    const cacheReload = createCacheReload< NormalizedContent >( {
        binding: webinars,
        loading,
        latch: initLatch,
        canRead: () => isAuthenticated.value,
        apply( rows: NormalizedContent[] ): void {
            // Keyed by id as the merge is, so a row the cache holds twice still shows once.
            const byId = new Map< string, Webinar >();
            rows.forEach( ( row: NormalizedContent ): void => {
                const webinar: Webinar = hydrateContent( row, appStore.compositionCtx );
                byId.set( webinar.contentfulId, webinar );
            } );

            allWebinars.value = filterAndSortWebinars( byId.values() );
            // The sync worker filled the cache after a failed load: the list is no longer "couldn't load".
            listLoad.succeed();
        },
        reinitialize: () => {
            reset();
            return initialize();
        },
        report: ( err: unknown ) => clientStateManager.queueClientError( err, {} )
    } );

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        allWebinars.value.splice( 0 );
        fetching.value = false;
        loading.value = false;
        initLatch.clear();
        cacheReload.cancel();
        listLoad.reset();
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value || initLatch.isHalted() ) {
            return;
        }

        loading.value = true;
        listLoad.start();
        const t0: number = performance.now();

        if ( isAuthenticated.value ) {
            try {
                // Wait for the database to open before the first read.
                await cache.ready();

                if ( !await webinars.isEmpty() ) {
                    const loadedWebinars: Webinar[] = ( await webinars.all() )
                        .map( ( item: NormalizedContent ) => hydrateContent( item, appStore.compositionCtx ) );
                    mergeFilterAndSortWebinarCatalog( loadedWebinars );

                    const t1: number = performance.now();
                    logger.info( `(local) Loaded ${ loadedWebinars.length } webinars in ${ t1 - t0 } ms` );
                } else {
                    const fetched: Webinar[] = await refreshWebinarsFromServer( formatDateAsISO8601String( DateTime.utc().plus( { days: -90 } ) ) );
                    mergeFilterAndSortWebinarCatalog( fetched );

                    const t1: number = performance.now();
                    logger.log( `Fetching & mapping ${ fetched.length } webinars took ${ t1 - t0 } ms` );
                }
                listLoad.succeed();
            } catch ( err: any ) {
                // A newer build evicted this context: stop quietly, the reload is already in flight.
                if ( err instanceof CacheClosedError ) {
                    initLatch.halt( err );
                    return;
                }

                // If anything went wrong while attempting to initialize the store, reset it and then handle the specific error
                reset();
                initLatch.halt( err );   // after reset(), which clears it: a cache failure stays latched

                clientStateManager.queueClientError( err, {} );
                listLoad.fail( err );   // after reset(), which clears it
            } finally {
                loading.value = false;
            }
        } else {
            if ( allWebinars.value.length > 0 ) {
                return;
            }

            // hydrateContent resolves author/categories/products/tags eagerly and returns a plain
            // snapshot, so anything hydrated before those catalogs land keeps empty associations
            // for the rest of the session — and `initialized` is derived from list length, so this
            // store never retries. Public routes no longer wait on the shell, which means this can
            // now fire at t=0: refreshWebinarsFromServer sends the request, then waits for the
            // composition catalogs before hydrating (UX roadmap #19).
            refreshWebinarsFromServer( formatDateAsISO8601String( DateTime.utc().plus( { days: -365 } ) ) )
                .then( ( webinars: Webinar[] ) => {
                    mergeFilterAndSortWebinarCatalog( webinars );

                    const t1: number = performance.now();
                    logger.log( `Fetching & mapping ${ webinars.length } webinars took ${ t1 - t0 } ms` );
                    listLoad.succeed();
                } ).catch( ( err: any ) => {
                    reset();
                    clientStateManager.queueClientError( err, {} );
                    listLoad.fail( err );
                } ).finally( () => loading.value = false );
        }
    }

    /**
     * Re-reads the webinars the sync worker just wrote, in place: see createCacheReload. Replaces the
     * `reset()` + `initialize()` pair UPDATE_COMPLETE used, which emptied the catalog for the read and unmounted a
     * playing webinar on the Webinar page and the Sector Edge carousel.
     */
    function reloadFromCacheAsync(): Promise< void > {
        return cacheReload.reloadFromCacheAsync();
    }

    /**
     * "Try again" on a list that failed to load. An explicit retry re-arms a latched cache failure, as reset() would,
     * without emptying anything first.
     */
    function retryLoad(): Promise< void > {
        initLatch.clear();
        return initialize();
    }

    /**
     * The webinar for `slug`: from the catalog when it is there, otherwise from the server. Resolves undefined when the
     * server has no such webinar for this visitor, and rejects when the request failed (reported here), so the page can
     * tell "isn't available" from "couldn't load". An abort through `signal` (a newer slug) rejects silently.
     */
    async function fetchWebinarBySlugIfMissing( slug: string, { signal }: { signal?: AbortSignal } = {} ): Promise< Webinar | undefined > {
        // Best case scenario is that the article has already been loaded and is immediately available
        let requestedWebinar: Webinar | undefined = allWebinars.value.find( ( w: Webinar ) => w.slug === slug );
        if ( requestedWebinar ) {
            return requestedWebinar;
        }

        try {
            fetching.value = true;
            // UX roadmap #19: the request goes out first and the wait for the composition catalogs overlaps it, as in
            // Articles.fetchArticleBySlugIfMissing. The page used to await the whole initialization before calling in.
            // tmi-ssq: a 503 before the server's first cache fill is retried once after its Retry-After, not shown as the
            // error page (a deep link opened during a content restart).
            const webinarRequest =
                retryOnceOnServiceUnavailable(
                    () => goFetch< Webinars, ContentCatalogRequest >( {
                        serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                        // The 3c2544b6 "refresh" selection set was the full one MINUS `publish_at`;
                        // both of this store's reads now take the full document and ignore the extra
                        // field, so the split disappears (bead tmi-d2v).
                        endpoint: 'content/webinar-catalog',
                        payload: {
                            skip: 0,
                            limit: 1,
                            contentFilter: {
                                slugs: [ slug ]
                            }
                        },
                        authToken: authToken.value,
                        // A timeout or a dropped connection resolved null, and the page said the webinar "isn't available" (errors-4).
                        forceThrowExceptions: true,
                        signal
                    } ),
                    { signal } );

            const [ response ] = await Promise.all( [ webinarRequest, appStore.compositionReadyAsync() ] );

            if ( null != response?.data?.webinarCatalog?.webinars ) {
                response.data.webinarCatalog.webinars
                    .map( ( w: ContentfulWebinar ) => {
                        const normalized = normalizeWebinar( w );
                        if ( normalized ) {
                            requestedWebinar = hydrateContent( normalized, appStore.compositionCtx );
                        }
                    } );
            }
        } catch ( e: any ) {
            // The page shows a "couldn't load" state for this, so it is reported, not only logged. The caller moving on
            // to another slug is not a failure.
            if ( !signal?.aborted ) {
                logger.error( '[ Webinars ] the webinar request failed', e );
                clientStateManager.queueClientError( e, { slug } );
            }
            throw e;
        } finally {
            fetching.value = false;
        }

        return requestedWebinar;
    }

    async function refreshWebinarsFromServer( latestUtcDate: string ): Promise< Webinar[] > {
        let retrievedWebinars: Webinar[] = [];

        // tmi-p00: a 503 before the server's first cache fill is retried once after its Retry-After, not shown as a failure.
        const listRequest =
            retryOnceOnServiceUnavailable(
                () => goFetch< Webinars, ContentCatalogRequest >( {
                    serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                    endpoint: 'content/webinar-catalog',
                    payload: {
                        skip: 0,
                        limit: 250,
                        contentFilter: {
                            fromPublishedDateTimeUTC: latestUtcDate
                        }
                    },
                    authToken: authToken.value,
                    // data-12: a timeout or a dropped connection resolved null, and the list read as "No results found".
                    forceThrowExceptions: true
                } ) );

        // Sent before the wait: hydration is the only step that needs the composition catalogs (UX roadmap #19).
        const [ response ] = await Promise.all( [ listRequest, appStore.compositionReadyAsync() ] );

        if ( null != response?.data?.webinarCatalog?.webinars ) {
            response?.data.webinarCatalog.webinars
                .map( ( webinarContent: ContentfulWebinar ) => {
                    const normalized = normalizeWebinar( webinarContent );
                    if ( normalized ) {
                        retrievedWebinars.push( hydrateContent( normalized, appStore.compositionCtx ) );
                    }
                } );
        }

        return retrievedWebinars;
    }

    function webinarBySlug( slug: string ): Webinar | undefined {
        return allWebinars.value.find( ( webinar: Webinar ): boolean => webinar.slug === slug );
    }

    function webinarById( id: string ): Webinar | undefined {
        return allWebinars.value.find( ( webinar: Webinar ): boolean => webinar.contentfulId === id );
    }

    /** Each webinar with its session start, soonest first. One without a usable start is skipped, not read as now. */
    function scheduledWebinars(): Array< { webinar: Webinar, startsAt: DateTime } > {
        return allWebinars.value
            .map( ( webinar: Webinar ) => ( { webinar, startsAt: webinarStartTime( webinar.webinarDate ) } ) )
            .filter( ( entry ): entry is { webinar: Webinar, startsAt: DateTime } => null !== entry.startsAt )
            .sort( ( e1, e2 ) => e1.startsAt.toMillis() - e2.startsAt.toMillis() );
    }

    /**
     * The next `webinarCount` sessions that have not ended (webinarPhase), soonest first.
     *
     * allWebinars is ordered by publishedDate, not by session date, so this sorts on webinarDate itself. The
     * old `.slice( -n - 1, -1 )` over the publish order always dropped the soonest session and returned [] when
     * only one was scheduled.
     *
     * Pass `now` from useMarketClock() inside a computed (rank 69): a read of the clock in here is not a dependency,
     * so the list froze at mount. The store does not hold the clock, which would keep it ticking for the session.
     */
    function nUpcomingWebinars( webinarCount: number, now: MarketInstant = Date.now() ): Webinar[] {
        return scheduledWebinars()
            .filter( ( { startsAt } ) => 'past' !== webinarPhase( startsAt, now ) )
            .slice( 0, Math.max( 0, webinarCount ) )
            .map( ( entry ) => entry.webinar );
    }

    /**
     * The sessions on the member's own calendar day of `now` that have not ended yet, soonest first (the same
     * permission-filtered catalog as the sidebar). Pass `now` from useMarketClock() inside a computed: the banner's
     * old computed read DateTime.local() once, so it kept yesterday's sessions in an open tab, and it kept a session
     * listed after it had ended (rank 69).
     */
    function webinarsLeftToday( now: MarketInstant ): Webinar[] {
        const today: string | null = toMarketTime( now ).toLocal().toISODate();
        return scheduledWebinars()
            .filter( ( { startsAt } ) => startsAt.toLocal().toISODate() === today && 'past' !== webinarPhase( startsAt, now ) )
            .map( ( entry ) => entry.webinar );
    }

    /**
     * Performs a merge and sort (based on publication date and time of the ITradeAlert) with the current collection of trade
     * alerts and any new trade alerts that are loaded.
     *
     * @param {Webinar[]} newWebinars - the newly loaded collection of Webinar objects that need to be added
     * @returns {Webinar[]} - the aggregated collection of Webinar objects sorted by their publication date & time
     */
    function mergeFilterAndSortWebinarCatalog( newWebinars: Webinar[] ): void {
        logger.debug( 'mergeAndSortWebinarCatalog - existing: %i, new: %i', allWebinars.value.length, newWebinars.length );

        const uniqueWebinars = new Map( allWebinars.value.map( ( webinar: Webinar ) => [ webinar.contentfulId, webinar ] ) );
        newWebinars.forEach( ( newWebinar: Webinar ): void => {
            // Always overwrite whatever was previously in the collection (fucking crazy editing process, why fix it
            // when you can just make everything else more fucking complicated)
            uniqueWebinars.set( newWebinar.contentfulId, newWebinar );
        } );

        allWebinars.value = filterAndSortWebinars( uniqueWebinars.values() );

        logger.debug( '[ Webinars ] mergeAndSortWebinarCatalog - combined: ', allWebinars.value.length );
    }

    /**
     * Drops the webinars the current user has not been granted access to and sorts the rest, newest first.
     */
    function filterAndSortWebinars( candidates: Iterable< Webinar > ): Webinar[] {
        return Array.from( candidates )
            // Filter out any content to which the current user has not been granted access
            .filter( ( webinar: Webinar ) => {
                if ( !isFreelyAvailableContent( webinar.products.map( p => p.slug ) ) ) {
                    return isAuthenticated.value && webinar.products.some( ( p: Product ) =>
                        !!( p.codes ) && permissionsStore.hasAccessBasedOnShortCode( p.codes! )
                    );
                }

                return true;
            } )
            .sort( ( w1: Webinar, w2: Webinar ) => w2.publishedDate?.localeCompare( w1.publishedDate ) ?? -1 );
    }

    // This is implemented as a computed property to avoid unnecessarily calculating it multiple times
    const webinarCategories = computed< Category[] >( () => {
        // Contains the full catalog of all webinars the current user has access to with all dependent objects associated
        const categoryCountMap: Map< string, Countable< Category > > = new Map< string, Countable< Category > >();
        allWebinars.value.forEach( ( item: ContentfulItem ): void => {
            item.categories.forEach( ( category: Category ): void => {
                if ( !categoryCountMap.has( category.slug ) ) {
                    categoryCountMap.set( category.slug, {
                        item: category,
                        count: 0
                    } );
                }

                categoryCountMap.get( category.slug )!.count++;
            } );
        } );

        const categoriesByObservationCount: Countable< Category >[] = [ ...categoryCountMap.values() ];
        const categoriesByAscendingObservationCount: Countable< Category >[] =
            categoriesByObservationCount.sort( ( container1: Countable< Category >, container2: Countable< Category > ) => container1.count - container2.count );

        return categoriesByAscendingObservationCount.map( ( container: Countable< Category > ) => container.item );
    } );

    return {
        // ACTIONS
        fetchWebinarBySlugIfMissing,
        initialize,
        nUpcomingWebinars,
        reloadFromCacheAsync,
        reset,
        retryLoad,
        webinarById,
        webinarBySlug,

        // GETTERS
        allWebinars,
        initialized,
        isLoading,
        loadError: listLoad.error,
        loadFailure: listLoad.failure,
        loadStatus: listLoad.status,
        webinarCategories,
        webinarsLeftToday
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useWebinarsStore, import.meta.hot ) );
}
